/**
 * @packageDocumentation
 * The Host SDK is designed for integration within the host application that serves as the container for Myop  components.<br/><br/>
 * It provides essential functionalities and APIs for registering, managing, and communicating with Myop components.<br/><br/>
 * This SDK enables seamless embedding of independently developed components into the host application, ensuring smooth interoperability and consistent user experience across various Myop component implementations.
 */
import {defineDynamicImport} from "../dynamicImport/defineDynamicImport";
import {IMyopLoader} from "./loaders/IMyopLoader";
import {IMyopComponent} from "./components/IMyopComponent";
import {MyopMessages} from "../common/MyopMessages";
import {
    BaseMyopMessage,
    EnvelopedMessage, ExecuteComponentMethod,
    InitMessage,
    InitResponse, MessageDirection, Ref
} from "../messages";
import {MyopIframeLoader} from "./loaders/iframeLoader";
import {WebcomponentLoader} from "./loaders/webcomponentLoader";
import type {IframeSDK} from "../Iframe";
import {connectProps, connectRefs, createRefComponent} from "./componentCore";
import {
    ExperienceTypes,
    IComponentConfig,
    IComponentDefinitionConfig,
    IDedicatedSkinConfig, IExperience, IFunctionResultsSegment, ISegmentedSkinConfig, ISegmentExperience,
    ISkinConfig, ISkinSelectorConfig, SkinSelectorTypes,


} from "../common";
// @ts-ignore handled by custom esbuild plugin
import version from "myop-sdk-version";
import {HTMLComponentLoader} from "./loaders/HTMLComponentLoader";
import {checkMyopOverride} from "./myopOverride.ts";

interface componentLoaderCreator {
    new(loaders: IMyopLoader[]): any;
}

// const getComponentDefinitionConfig = async (id: string) => {
//     const res = await fetch(`http://localhost:3000/component?id=${id}`);
//     const componentDef: IComponentDefinitionConfig = (await res.json()).item;
//     return componentDef
// }

//const __ROOT_SDK_PUBLIC_URL__ = 'https://dfdlk2p1m187v.cloudfront.net/myop_sdk.min.js'
const __ROOT_SDK_PUBLIC_URL__ = 'https://cdn.myop.dev/sdk/next/myop_sdk.min.js'

/**
 * The Host SDK is designed for integration within the host application that serves as the container for Myop  components.<br/><br/>
 * It provides essential functionalities and APIs for registering, managing, and communicating with Myop components.<br/><br/>
 * This SDK enables seamless embedding of independently developed components into the host application, ensuring smooth interoperability and consistent user experience across various Myop component implementations.
 */
export class HostSDK {
    static Instance = () => window.myop.hostSDK;

    components2init: IMyopComponent[] = [];
    components: IMyopComponent[] = [];
    componentsLoaders: IMyopLoader[] = [];

    public initiated: boolean = false;

    public version = version;

    type2InstanceCount: Record<string, number> = {};
    public assignId = (componentDefinition: IComponentDefinitionConfig) => {
        if (!this.type2InstanceCount[componentDefinition.id]) {
            this.type2InstanceCount[componentDefinition.id] = 0;
        }
        return `${componentDefinition.id}_${++this.type2InstanceCount[componentDefinition.id]}`;
    }

    //TODO : fix the inspect implementation inside refComponent
    //then inspected maybe can be dropped
    private inspected = false;
    public inspect = () => {
        if (this.inspected)
            return;

        this.inspected = true;

        this.components.forEach(component => {
            if (component.inspect) {
                component.inspect()
            }
        })
    }

    private incomingMessageHandler = (event: MessageEvent<BaseMyopMessage>) => {
        //console.log('incomingMessageHandler GOT ', event)
        //TODO :  if (event.origin !== "http://example.org:8080") return;
        if (event.origin === document.location.origin) {
            //console.log('I Know that !!!');
            //  return;
        }
        //console.log("event on host side ", event);

        if (event.data.myop) {
            if (event.data.direction !== MessageDirection.up && event.data.messageType !== MyopMessages.EnvelopedMessage) {
                //console.log('message dropped in hostsdk, direction was ', event.data.direction)
                return;
            }
            // console.log("event on host side ", event);

            const myopMessage: BaseMyopMessage = event.data;

            if (myopMessage.messageType === MyopMessages.InitRequest) {
                this.components2init.forEach(component => {
                    component.send(new InitMessage(component.id))
                });
                return;
            }

            if (myopMessage.messageType === MyopMessages.InitResponse) {
                // remove from components 2 init
                // @ts-ignore
                this.components2init = this.components2init.filter(component => component.id !== (myopMessage as InitResponse).content.id)

                const initiatedComponent = this.components.find(component => component.id === (myopMessage as InitResponse).content?.id);
                initiatedComponent?.setInitiated()
                //initiatedComponent?.parent?.setInitiated();
                // TODO : is it true to not pass further ( to the component )
                return;
            }

            if (myopMessage.messageType === MyopMessages.EnvelopedMessage) {
                //debugger;
                const typedMessage = myopMessage as EnvelopedMessage
                const refComponent = this.components.find(component => component.id === typedMessage.destination);

                if (refComponent) {
                    if (typedMessage.direction === MessageDirection.down) {
                        if (typedMessage.message.messageType === ExecuteComponentMethod.name) {
                            refComponent.onMessageReceived(typedMessage.message)
                        } else {
                            //
                            typedMessage.message.route = typedMessage.route;
                            refComponent.send(typedMessage.message);
                        }
                        return;
                    }
                } else {
                    const innerComponent = this.components.find(component => component.id === typedMessage.message.destination);
                    if (innerComponent) {
                        innerComponent.send(typedMessage.message)
                    } else {
                        // TODO : log ?
                    }
                }
                // @ts-ignore
                //component.refs.body.onMessageReceived(myopMessage.message);

                return;
            }

            const component = this.components.find(component => component.id === myopMessage.destination);

            if (!component) {
                //no in mem component
                //pass to parent HostSDK to try process there
                if (window.myop.IframeSDK) {
                    window.myop.IframeSDK.messageToHost(myopMessage);
                }
                return;
            }

            if (component) {
                if (component.onMessageReceived(myopMessage)) {
                    return;
                } else {
                    //not processed in onMessageReceived
                    //pass to parent HostSDK to try process there
                    if (window.myop.IframeSDK)
                        window.myop.IframeSDK.messageToHost(myopMessage);
                }
            }
        }

        // event.source.postMessage(
        //     "hi there yourself!  the secret response " + "is: rheeeeet!",
        //     event.origin,
        //   );
    }

    public send = (message: BaseMyopMessage) => {
        const syntheticEvent = new MessageEvent<BaseMyopMessage>('',
            {data: message}
        )

        this.incomingMessageHandler(syntheticEvent);
    }

    public init = (customComponentLoaderCreators?: componentLoaderCreator[]) => {
        if (this.initiated) {
            throw new Error('hostSDK already initiated');
        }

        const basicLoaders = [
            new MyopIframeLoader(),
            new WebcomponentLoader(),
            new HTMLComponentLoader()
        ];

        this.componentsLoaders = [
            ...basicLoaders
        ];

        if (customComponentLoaderCreators) {
            //debugger;
            const loaderByType: Record<string, IMyopLoader> = {};

            customComponentLoaderCreators.forEach(customComponentLoaderCreator => {
                const customLoader = new customComponentLoaderCreator(this.componentsLoaders);
                loaderByType[customLoader.type] = customLoader;
            })

            this.componentsLoaders.forEach(loader => {
                if (!loaderByType[loader.type])
                    loaderByType[loader.type] = loader;
            })

            this.componentsLoaders = Object.values(loaderByType);
        }

        // Incomming messages from IFrames
        window.addEventListener(
            "message",
            this.incomingMessageHandler,
            false,
        );

        console.log('myop hostSDK initiated', document.location.href);
        this.initiated = true;
    }

    private _getSkinIdBySkinSelector = async (skinSelector: ISkinSelectorConfig, experiences: IExperience[] = [], hooks?: loaderHooks): Promise<string> => {
        async function asyncFind<T>(
            array: T[],
            predicate: (item: T) => Promise<boolean>
        ): Promise<T | undefined> {
            for (const item of array) {
                if (await predicate(item)) {
                    return item;
                }
            }
            return undefined; // Return undefined if no match is found
        }

        switch (skinSelector.type) {
            // @ts-ignore
            case "DedicatedSkin" : //backward compatibility
            case SkinSelectorTypes.Dedicated: {
                let selectedSkinId = (skinSelector as IDedicatedSkinConfig).skin?.id
                    || (skinSelector as IDedicatedSkinConfig).skins![0].id;

                return selectedSkinId;
            }
                break;
            case SkinSelectorTypes.Segmented: {
                const typedSkinSelector = (skinSelector as ISegmentedSkinConfig)
                const segment = await asyncFind(typedSkinSelector.segments, async segment => {
                    if (segment.segmentId === 'Default') {
                        return true;
                    }

                    const experience = experiences.find(experience => {
                        if (experience.type === ExperienceTypes.Segment) {
                            return (experience as ISegmentExperience).segment.id === segment.segmentId
                        }
                        return false;
                    });

                    if (!experience) {
                        throw new Error("experience not found");
                    }

                    const typedExperience = experience as ISegmentExperience;
                    const typedSegment = (typedExperience.segment as IFunctionResultsSegment)

                    const segmentFunction = (hooks ? hooks : {})[typedSegment.function];
                    if (segmentFunction) {
                        const results = await segmentFunction();
                        return results;
                    } else {
                        console.warn(`segmentId:${segment.segmentId}, ${typedSegment.function} function not provided !`)
                    }

                    return false;
                });

                if (segment)
                    return this._getSkinIdBySkinSelector(segment.skinSelector, experiences);
                else {
                    debugger;
                }
            }
                break;
            default: {
                debugger;
            }
        }
        return '' //TODO
    }

    private runSkinSelector = async (componentConfig: IComponentConfig, hooks?: loaderHooks) => {
        const skinId = await this._getSkinIdBySkinSelector(componentConfig.instance.skinSelector, componentConfig.instance.resolvedExperiences, hooks);
        return componentConfig.type.skins.find(skin => skin.id === skinId);
    }

    public loaderHooks?: loaderHooks;

    public loadComponent = async <T extends IMyopComponent = IMyopComponent>(componentConfig: IComponentConfig, container?: HTMLElement | Ref, options?: loaderOptions):
        Promise<T> => {

        if (!container) {
            debugger;
            throw new Error('no container was found for this component');
        }

        const overriddenConfig = await checkMyopOverride(componentConfig);
        if (overriddenConfig) {
            //TODO : log
            componentConfig = overriddenConfig;
        }

        // // @ts-ignore
        // if (!container.__nonProxied) {
        //     const refComponent = (container as unknown as MyopRefComponent);
        //     //if we got loadComponent over MyopRefComponent
        //     if (refComponent.id && refComponent.parent) {
        //         return this.loadComponent(componentConfig, refComponent.ref, options);
        //
        //         // let rootComponent: IMyopComponent = refComponent;
        //         // while(!rootComponent.element && rootComponent.parent){
        //         //     rootComponent = rootComponent.parent
        //         // }
        //         //
        //         // rootComponent.element
        //         // //what now ?
        //     }
        // }

        const hooks = {...(this.loaderHooks ?? {}), ...(options?.hooks ?? {})};

        const ref = container as Ref;
        if (ref.myop) {
            if (ref.component) {

                componentConfig = {...componentConfig};
                componentConfig.instance = {
                    ...componentConfig.instance
                }

                let selectedSkin = await this.runSkinSelector(componentConfig, hooks)!
                if (!selectedSkin) {
                    throw new Error("runSkinSelector failed to choose skin, check the provided config");
                }

                if (options?.hooks?.afterSkinSelected) {
                    selectedSkin = await options.hooks.afterSkinSelected({...selectedSkin});
                }

                componentConfig.instance.skinSelector = {
                    type: SkinSelectorTypes.Dedicated,
                    skin: selectedSkin
                    //  skins: [selectedSkin]
                } as IDedicatedSkinConfig

                if (options) {
                    options = {...options}
                    delete options.hooks
                }

                const refComponent = await createRefComponent<T>(ref, componentConfig, this.components, options);

                //TODO : connect refs and props insode createRefComponent, elsewhere its not really <T>
                return refComponent;
            } else {
                throw new Error('load component got a detached ref.')
            }
        }

        container = container as HTMLElement;

        /**
         *
         * choose skin on runtime ...
         *        componentConfig.instance.skinSelector.type
         */

        let selectedSkin = await this.runSkinSelector(componentConfig, hooks)!

        if (!selectedSkin) {
            throw new Error("runSkinSelector failed to choose skin, check the provided config");
        }

        if (options?.hooks?.afterSkinSelected) {
            selectedSkin = await options.hooks.afterSkinSelected({...selectedSkin});
        }
        const componentLoader = this.componentsLoaders.find(loader => loader.type === selectedSkin.loader.type);

        if (!componentLoader) {
            debugger;
            throw new Error(`no loader "${selectedSkin.loader.type}" was found for component`);
        }

        //for loaded webcomponent modules to following line creates a race condition with the init message flow
        let component = await componentLoader.load(componentConfig.type, selectedSkin, container, options);

        // TODO : when this is not true ?
        if (!component.isInitiated)
            this.components2init.push(component)

        component.bind('onDispose', () => {
            //console.log('component dispose', component);
            this.components = this.components.filter(c => c !== component)
            this.components2init = this.components2init.filter(c => c !== component)
            return true;
        })

        this.components.push(component);

        // true by default
        if (!options || options.connectProps !== false) {
            //TODO : make type field conditional type infer "string" > string, "number" > number, etc...
            component = connectProps(component, componentConfig.type.props, selectedSkin);
        }

        try {
            //TODO : make optional?
            //TODO : fix issues with timeout and wrong ref names from config vs current skin
            await connectRefs(component, componentConfig, this.components);
        } catch (e) {
            throw e;
            //debugger;
            // TOOD : throw ?
        }
        if (!options?.skipInit)
            await component.initiated();

        return component as T;
    }

    public navigate = async <T extends IMyopComponent = IMyopComponent>(component: IMyopComponent, newComponentConfig: IComponentConfig, options?: navigationOptions) => {
        if (component.parent) {
            throw new Error('Navigating on remote ref components is not implemented yet');
        }

        //component.markedForDisposed = true;

        // if (options?.inplace) {
        //     if (component.element) {
        //         let newComponent = await this.loadComponent<T>(newComponentConfig, component.element);
        //         await newComponent.initiated();
        //
        //         if (options.init)
        //             await options.init(newComponent);
        //
        //         return newComponent;
        //     } else {
        //         throw new Error('...')
        //     }
        // }

        if (options?.staged) {
            //const selectedSkin = this.runSkinSelector(newComponentConfig)
            let newComponent = await this.loadComponent<T>(newComponentConfig, component.container, {
                hidden: true,
                relative: {direction: 'after', child: component.element!},
                ...options
            });

            if (options.init)
                await options.init(newComponent);

            newComponent.show();
            component.dispose();
            return newComponent;
        } else {
            const nextSibling = component.element!.nextSibling;

            component.dispose();
            return this.loadComponent<T>(newComponentConfig, component.container, {
                relative: {
                    direction: "before",
                    child: nextSibling,
                },
                ...options
            });
        }
    }
}

export type navigationOptions = {
    staged?: boolean,
    //inplace?: boolean,
    init?: (component: IMyopComponent) => Promise<void>
    skipInit?: boolean
} & loaderOptions

type relativeLoadConfigDirection = 'before' | 'after';


export type loaderHooks = {
    afterSkinSelected?: (selectedSkin: ISkinConfig) => Promise<ISkinConfig>
} &
    // segmentation hooks
    ({ [key: string]: () => Promise<boolean> | boolean } | {})

//TODO: support serializable version of that object
export interface relativeLoadConfig {
    //If this is null, then newNode is inserted at the end of node's child nodes.
    //https://developer.mozilla.org/en-US/docs/Web/API/Node/insertBefore
    child: ChildNode | null
    direction: relativeLoadConfigDirection
}

export interface IEnvironment {
    [key: string]: any
}

type ElementAttributes = {
    [key: string]: string | number | boolean | undefined | null;
};

export interface loaderOptions {
    id?: string
    hidden?: boolean
    connectProps?: boolean
    timeout?: number
    skipInit?: boolean
    relative?: relativeLoadConfig
    hooks?: loaderHooks
    elementAttributes?: ElementAttributes
    _environment?: IEnvironment
    _hasParent?: boolean
    _inspection?: boolean
    data?: any
    /**
     * Raw CSS injected into the component's iframe <head> before first paint.
     * Appended last so it overrides the component's own styles via cascade order.
     * Useful for theming v2 (HTML) components — colors, fonts, spacing, etc.
     */
    injectCss?: string
    /**
     * Control iframe auto-sizing behavior.
     * - true (default): iframe auto-sizes to content height, width fills parent.
     * - false: no sizing logic runs. No observers, no measurements, no style changes.
     *   The iframe stays at position:absolute filling the container.
     *   The host is fully responsible for container dimensions.
     */
    autoSize?: boolean
}

if (!window.myop) {
    // @ts-ignore
    window.myop = {
        __ROOT_SDK_PUBLIC_URL__
    };
}

if (!window.myop.hostSDK) {
    const _hostSDK = new HostSDK();
    //TODO: enable lazy init?
    _hostSDK.init();

    if (!window.myop) {
        // @ts-ignore
        window.myop = {
            __ROOT_SDK_PUBLIC_URL__
        };
    }
    window.myop.hostSDK = _hostSDK;
}

// @ts-ignore
export const hostSDK: HostSDK = window.myop.hostSDK;

defineDynamicImport('hostSDK', hostSDK);

declare global {
    interface Window {
        myop: {
            __ROOT_SDK_PUBLIC_URL__: string,
            hostSDK: HostSDK,
            IframeSDK: InstanceType<typeof IframeSDK>
        }
    }
}
